import React, { ReactElement, useEffect } from 'react';
import { isSome, Option } from 'fp-ts/lib/Option';
import { FullScreen, FullScreenHandle, useFullScreenHandle } from 'react-full-screen';

import { ErrorData } from 'domain/types';
import { useNavigation } from 'adapter/in/dashboards/hooks/useNavigation';
import useAutorefreshDashboard from 'adapter/in/dashboards/hooks/Dashboard/useAutorefreshDashboard';
import { useReadDashboard } from 'adapter/in/dashboards/hooks/Dashboard/useReadDashboard';
import { useReadWidgets } from 'adapter/in/dashboards/hooks/Widgets/useReadWidgets';
import { DashboardResult } from 'adapter/in/dashboards/context/Dashboard';
import { dispatchWidgetsView } from 'adapter/in/dashboards/context/Widgets/view';
import DashboardPage from 'adapter/in/dashboards/pages/Dashboard';
import styled from 'styled-components';
import { useAlertToaster } from 'adapter/in/common/context/Alert';

type DashboardContainerProps = {
    id: string;
};

type UseDashboardContainer = {
    isLoading: boolean;
    data: DashboardResult;
    error: Option<ErrorData>;
    handleFullscreen: FullScreenHandle;
    hasWidgets: boolean;
};

export const useDashboardContainer = (id: string): UseDashboardContainer => {
    const { isLoading, data, error, success, reset: readDashboardReset } = useReadDashboard(id);
    const { isLoading: readWidgetsIsLoading, data: readWidgetsResult, error: readWidgetsError, reset: readWidgetsReset } = useReadWidgets(
        id
    );
    const { setWidgets } = dispatchWidgetsView();
    const { navigationPush } = useNavigation();

    useAutorefreshDashboard();

    useEffect(() => {
        if (success && isSome(data) && readWidgetsResult && isSome(readWidgetsResult)) {
            if (readWidgetsResult.value.length > 0) {
                setWidgets(readWidgetsResult.value);
            } else if (id === data.value.id && isSome(data) && data.value.permittedActions.edit) {
                navigationPush(`${id}/edit`);
            }
        }
    }, [data, readWidgetsResult]);

    const onUnmountResetState = () => {
        readDashboardReset();
        readWidgetsReset();
    };

    useEffect(() => onUnmountResetState, []);

    return {
        isLoading: isLoading || readWidgetsIsLoading,
        data,
        hasWidgets: isSome(readWidgetsResult) && readWidgetsResult.value.length > 0,
        error: isSome(error) ? error : readWidgetsError,
        handleFullscreen: useFullScreenHandle(),
    };
};

export const StyledFullScreen = styled(FullScreen)`
    height: 100%;
`;

export const DashboardContainer = (props: DashboardContainerProps): ReactElement => {
    const { id } = props;
    const { data, isLoading, error, handleFullscreen, hasWidgets } = useDashboardContainer(id);
    const { message: alertMessage, show: showAlert, type: alertType, style } = useAlertToaster();

    return (
        <StyledFullScreen handle={handleFullscreen}>
            <DashboardPage
                data={data}
                hasWidgets={hasWidgets}
                isLoading={isLoading}
                error={error}
                onFullscreen={handleFullscreen.enter}
                fullscreenActive={handleFullscreen.active}
                alertType={alertType}
                alertMessage={alertMessage}
                animationStyle={style}
                showAlert={showAlert}
            />
        </StyledFullScreen>
    );
};
